import React, { useState } from 'react';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { auth } from '../firebase/firebaseConfig';
import { useNavigate, Link } from 'react-router-dom';
import { registerUser } from '../services/api';
import { PageHeading, Text } from '../components/ui';
import { useAuth } from '../context/authContext';

const Register = () => {
    const [email, setEmail] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [termsAccepted, setTermsAccepted] = useState(false);
    const navigate = useNavigate();
    const { setRegistrationState, refreshUserProfile } = useAuth();

    const handleRegister = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        
        // Client-side validation
        if (password.length < 6) {
            setError('Password should be at least 6 characters long.');
            setLoading(false);
            return;
        }

        // Validate display name
        if (!displayName.trim()) {
            setError('Please enter a display name.');
            setLoading(false);
            return;
        }

        // Validate display name format (alphanumeric, underscore, hyphen, spaces allowed)
        const displayNameRegex = /^[a-zA-Z0-9_\-\s]+$/;
        if (!displayNameRegex.test(displayName.trim())) {
            setError('Display name can only contain letters, numbers, spaces, hyphens, and underscores.');
            setLoading(false);
            return;
        }

        // Validate terms acceptance
        if (!termsAccepted) {
            setError('You must agree to the Terms of Service and Privacy Policy to create an account.');
            setLoading(false);
            return;
        }
        
        try {
            // Set registration state to prevent race conditions
            setRegistrationState(true);
            
            // Step 1: Create user in Firebase
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;
            
            // Step 1.5: Update Firebase Auth displayName immediately so Slack notification can use it
            try {
                await updateProfile(user, {
                    displayName: displayName.trim()
                });
            } catch (profileError) {
                console.warn('Failed to update Firebase profile displayName:', profileError);
                // Don't fail registration if this fails - it's not critical
            }
            
            // Step 2: Get Firebase token
            const token = await user.getIdToken();
            
            // Step 3: Register user in our database with retry logic
            const userData = {
                username: displayName.trim(),
                display_name: displayName.trim()
            };
            
            try {
                await registerUser(userData, token);
                
                // Wait a moment for database to commit the registration
                await new Promise(resolve => setTimeout(resolve, 500));
                
                // Clear registration state
                setRegistrationState(false);
                
                // Refresh user profile to ensure it's available for components
                // This will retry if there's a race condition
                await refreshUserProfile();
                
                // Wait a moment for auth context to update with the new profile
                // This ensures components don't try to fetch data before user is registered
                await new Promise(resolve => setTimeout(resolve, 300));
                
                // Redirect new users to Explore page for walkthrough
                navigate('/explore');
                  } catch (dbError) {
                      console.error('❌ Database registration failed:', dbError);
                      setRegistrationState(false);
                      
                      // If user already exists, that's actually success - just redirect
                      if (dbError.message?.includes('User ID already exists') || 
                          dbError.message?.includes('USER_ID_CONFLICT')) {
                          console.log('✅ User already exists in database, proceeding...');
                          // Redirect new users to Explore page for walkthrough
                          navigate('/explore');
                          return;
                      }
                      
                      throw dbError; // Re-throw to trigger the Firebase rollback
                  }
        } catch (err) {
            console.error('❌ Registration error:', err);
            
            // Clear registration state on error
            setRegistrationState(false);
            
            // Provide user-friendly error messages
            if (err.message?.includes('email-already-in-use')) {
                setError('This email is already registered. Please try logging in instead.');
            } else if (err.message?.includes('weak-password')) {
                setError('Password should be at least 6 characters long.');
            } else if (err.message?.includes('invalid-email')) {
                setError('Please enter a valid email address.');
            } else {
                setError(err.message || 'Registration failed. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    // Eye icon component for password visibility
    const EyeIcon = ({ size = 20, className = '' }) => (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="currentColor"
            className={className}
            aria-hidden="true"
            style={{ border: 'none', outline: 'none' }}
        >
            <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
        </svg>
    );

    const EyeOffIcon = ({ size = 20, className = '' }) => (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill="currentColor"
            className={className}
            aria-hidden="true"
            style={{ border: 'none', outline: 'none' }}
        >
            <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"/>
        </svg>
    );

    return (
        <div className="min-h-screen bg-neutral-50 px-0">
            <div className="w-full py-8">
                {/* Header */}
                <PageHeading 
                    size="hero"
                    subtitle="CREATE YOUR ACCOUNT"
                >
                    REGISTER
                </PageHeading>

                {/* Main Form Container */}
                <div className="bg-neutral-0 hairline minimal-padding vspace-xl mb-6 md:mb-8 mt-6 md:mt-8">
                    <form onSubmit={handleRegister} className="vspace">
                        {/* Display Name Field */}
                        <div>
                            <label htmlFor="displayName" className="block font-mono text-sm uppercase tracking-wider text-neutral-900 mb-2">
                                DISPLAY NAME
                            </label>
                            <input
                                id="displayName"
                                type="text"
                                placeholder="Enter your display name"
                                value={displayName}
                                onChange={(e) => setDisplayName(e.target.value)}
                                required
                                autoComplete="username"
                                className="w-full p-3 border border-neutral-900 bg-neutral-0 font-mono text-sm text-neutral-900 placeholder-neutral-600 focus:outline-none focus:border-neutral-900"
                                disabled={loading}
                            />
                            <Text size="xs" className="text-neutral-500 opacity-70 mt-1 font-mono text-xs">
                                This is the name others will see
                            </Text>
                        </div>

                        {/* Email Field */}
                        <div>
                            <label htmlFor="email" className="block font-mono text-sm uppercase tracking-wider text-neutral-900 mb-2">
                                EMAIL
                            </label>
                            <input
                                id="email"
                                type="email"
                                placeholder="your@email.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                autoComplete="email"
                                className="w-full p-3 border border-neutral-900 bg-neutral-0 font-mono text-sm text-neutral-900 placeholder-neutral-600 focus:outline-none focus:border-neutral-900"
                                disabled={loading}
                            />
                        </div>

                        {/* Password Field */}
                        <div>
                            <label htmlFor="password" className="block font-mono text-sm uppercase tracking-wider text-neutral-900 mb-2">
                                PASSWORD
                            </label>
                            <div className="relative">
                                <input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="Enter your password (min 6 characters)"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    minLength={6}
                                    autoComplete="new-password"
                                    className="w-full p-3 pr-12 border border-neutral-900 bg-neutral-0 font-mono text-sm text-neutral-900 placeholder-neutral-600 focus:outline-none focus:border-neutral-900"
                                    disabled={loading}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-900 opacity-60 hover:text-neutral-400 transition-colors duration-120 focus:outline-none border-none bg-transparent p-0 m-0"
                                    style={{ border: 'none', outline: 'none', background: 'transparent', padding: 0, margin: 0 }}
                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                    disabled={loading}
                                >
                                    {showPassword ? (
                                        <EyeOffIcon size={20} />
                                    ) : (
                                        <EyeIcon size={20} />
                                    )}
                                </button>
                            </div>
                            {password && password.length < 6 && (
                                <Text size="xs" className="text-red-600 mt-1 font-mono">
                                    Password must be at least 6 characters long
                                </Text>
                            )}
                        </div>

                        {/* Terms Acceptance */}
                        <div>
                            <label className="checkbox-label flex items-start space-x-2 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={termsAccepted}
                                    onChange={(e) => setTermsAccepted(e.target.checked)}
                                    className="mt-1"
                                    required
                                />
                                <span className="text-sm text-neutral-900">
                                    I agree to the{' '}
                                    <Link to="/terms-of-service" target="_blank" className="underline">
                                        Terms of Service
                                    </Link>
                                    {' '}and{' '}
                                    <Link to="/privacy-policy" target="_blank" className="underline">
                                        Privacy Policy
                                    </Link>.
                                </span>
                            </label>
                        </div>

                        {/* Error Message */}
                        {error && (
                            <div className="bg-red-50 p-4 hairline border-red-200">
                                <Text size="sm" className="text-red-900 font-mono">
                                    {error}
                                </Text>
                            </div>
                        )}

                        {/* Submit Button */}
                        <button 
                            type="submit" 
                            disabled={loading}
                            className={`w-full py-3 px-6 hairline font-mono text-sm uppercase tracking-wider transition-opacity duration-120 ${
                                loading 
                                    ? 'bg-neutral-400 text-neutral-600 cursor-not-allowed' 
                                    : 'bg-neutral-900 text-neutral-0 hover:opacity-60'
                            }`}
                        >
                            {loading ? 'REGISTERING...' : 'REGISTER'}
                        </button>
                    </form>

                    {/* Login Link */}
                    <div className="mt-8 pt-6 hairline-t text-center">
                        <Text size="sm" className="text-neutral-900 opacity-60 mb-2 font-mono">
                            Already have an account?
                        </Text>
                        <Link 
                            to="/login" 
                            className="font-mono text-sm uppercase tracking-wider text-neutral-900 opacity-60 hover:opacity-100 transition-opacity duration-120"
                        >
                            Login here →
                        </Link>
                    </div>
                </div>

                {/* Back to Home Link */}
                <div className="text-center">
                    <Link 
                        to="/" 
                        className="font-mono text-sm uppercase tracking-wider text-neutral-900 opacity-60 hover:opacity-100 transition-opacity duration-120"
                    >
                        ← Back to Home
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default Register;